import React, { useCallback } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Button, Stack, Typography } from '@lyearn/ui';
import { Image } from '@/components/Image';
import AddCoverDialog from '@/modules/corporate/components/AddCoverDialog';
import { CoverType } from '@/modules/corporate/components/ImageList/types';
import { CreateMediaInput, MediaKindEnum } from '@/types/schema';

import { ImageTypeEnum } from './const';
import { defaultCoverImageStyles } from './styles';

type AddCoverProps = {
  className?: string;
  cover?: CreateMediaInput;
  defaultCover: CreateMediaInput;
  onChange: (cover?: CreateMediaInput) => void;
  disabled?: boolean;
  title?: string;
  coverImageStyles?: string;
  overrideError?: boolean;
  staticImageType?: ImageTypeEnum;
  description?: string;
  useImg?: boolean;
  imageSize?: {
    width: number;
    height: number;
  };
};

function AddCover({
  className,
  cover,
  defaultCover,
  onChange,
  disabled,
  title,
  staticImageType,
  coverImageStyles = defaultCoverImageStyles,
  overrideError = false,
  description,
  useImg = false,
  imageSize = { width: 266, height: 200 },
}: AddCoverProps) {
  const { t } = useTranslation(['Events', 'Course']);
  const [showDialog, { toggle: toggleDialog }] = useToggle(false);
  const onSubmit = useCallback(
    (selectedCover?: CoverType) => {
      if (selectedCover?.url) {
        onChange({
          kind: MediaKindEnum.Image,
          url: selectedCover?.url,
          name: selectedCover?.name,
        });
      } else {
        onChange?.(undefined);
      }
    },
    [onChange],
  );

  const adaptedDescription = description ?? t('Events:Editor.CoverImageDescription');

  return (
    <>
      <Stack className={className} justifyContent="between">
        <Box>
          <Typography className="ps-8" color="text-text-primary" variant="heading-s">
            {title ?? t('Events:Editor.CoverImage')}
          </Typography>
          <Typography
            className="mt-8 w-256 ps-8"
            color="text-text-secondary"
            variant="body-short-01">
            {adaptedDescription}
          </Typography>
          {disabled ? null : (
            <Button className="mt-32" size="small" variant="outlined" onClick={toggleDialog}>
              {cover ? t('Events:Editor.ReplaceImage') : t('Common:Image.AddImage')}
            </Button>
          )}
        </Box>
        <Image
          alt="event cover"
          className={cn('border border-divider', { 'opacity-50': disabled }, coverImageStyles)}
          height={imageSize.height}
          overrideError={overrideError}
          src={cover?.url ?? defaultCover?.url!}
          useImg={useImg}
          width={imageSize.width}
        />
      </Stack>
      {showDialog ? (
        <AddCoverDialog
          cover={cover}
          staticImageType={staticImageType}
          onClose={toggleDialog}
          onSubmit={onSubmit}
        />
      ) : null}
    </>
  );
}

export default React.memo(AddCover);
